import React, { useState, useCallback } from "react"
import {
  Upload,
  Button,
  message,
  Image,
  Tag,
  Popconfirm,
  Tooltip,
  Typography,
  Space,
  Card,
  Row,
  Col,
  Select,
} from "antd"
import {
  InboxOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
  PaperClipOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileTextOutlined,
  DownloadOutlined,
} from "@ant-design/icons"
import type { RcFile } from "antd/es/upload/interface"
import fileStorageService, { MOVEMENT_ATTACHMENTS_BUCKET } from "../../services/fileStorageService"
import { useConfig } from "../../contexts/ConfigContext"
import { useCompany } from "../../contexts/CompanyContext"
import { useUser } from "../../contexts/UserContext"
import useAuth from "../../hooks/useAuth"
import { useSecureRequest } from "../../hooks/useSecureRequest"
import { TStorageMovementAttachment } from "../../models/storageModel"

const { Text } = Typography
const { Dragger } = Upload

const MAX_FILE_SIZE_MB = 20
const MAX_ATTACHMENTS_COUNT = 15

const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/svg+xml",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "text/plain",
  "text/csv",
])

const DEFAULT_DESCRIPTION_OPTIONS = [
  { value: "Factura de compra", label: "Factura de compra" },
  { value: "Remisión / Guía de despacho", label: "Remisión / Guía" },
  { value: "Comprobante de pago", label: "Comprobante de pago" },
  { value: "Orden de compra", label: "Orden de compra" },
  { value: "Soporte de entrega", label: "Soporte de entrega" },
  { value: "Nota de ajuste", label: "Nota de ajuste" },
  { value: "Otro documento", label: "Otro documento" },
]

export const getFileCategory = (mimeType: string, fileName: string = "") => {
  const ext = fileName.split(".").pop()?.toLowerCase() || ""
  if (mimeType.includes("pdf") || ext === "pdf") {
    return {
      type: "pdf",
      color: "#ff4d4f",
      bgColor: "#fff1f0",
      borderColor: "#ffa39e",
      label: "PDF",
      icon: <FilePdfOutlined style={{ fontSize: 28, color: "#ff4d4f" }} />,
    }
  }
  if (mimeType.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(ext)) {
    return {
      type: "image",
      color: "#1890ff",
      bgColor: "#e6f7ff",
      borderColor: "#91d5ff",
      label: "IMAGEN",
      icon: <FileImageOutlined style={{ fontSize: 28, color: "#1890ff" }} />,
    }
  }
  if (mimeType.includes("excel") || mimeType.includes("spreadsheet") || ["xlsx", "xls", "csv"].includes(ext)) {
    return {
      type: "excel",
      color: "#52c41a",
      bgColor: "#f6ffed",
      borderColor: "#b7eb8f",
      label: "EXCEL",
      icon: <FileExcelOutlined style={{ fontSize: 28, color: "#52c41a" }} />,
    }
  }
  if (mimeType.includes("word") || ["doc", "docx"].includes(ext)) {
    return {
      type: "word",
      color: "#2f54eb",
      bgColor: "#f0f5ff",
      borderColor: "#adc6ff",
      label: "WORD",
      icon: <FileWordOutlined style={{ fontSize: 28, color: "#2f54eb" }} />,
    }
  }
  return {
    type: "doc",
    color: "#722ed1",
    bgColor: "#f9f0ff",
    borderColor: "#d3adf7",
    label: "DOC",
    icon: <FileTextOutlined style={{ fontSize: 28, color: "#722ed1" }} />,
  }
}

export const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface MovementAttachmentUploaderProps {
  movementId?: number
  movementUid?: string
  value?: TStorageMovementAttachment[]
  onChange?: (attachments: TStorageMovementAttachment[]) => void
  disabled?: boolean
  directApi?: boolean
  title?: string
  defaultDocType?: string
}

const EMPTY_ATTACHMENTS: TStorageMovementAttachment[] = []

export const MovementAttachmentUploader: React.FC<MovementAttachmentUploaderProps> = ({
  movementId,
  movementUid,
  value = EMPTY_ATTACHMENTS,
  onChange,
  disabled = false,
  directApi = false,
  title = "Documentos Adjuntos y Soportes",
  defaultDocType,
}) => {
  const { config } = useConfig()
  const { company } = useCompany()
  const { user } = useUser()
  const { tokenData } = useAuth.useToken("authCsrfToken", config.session, config.key)
  const { securePost } = useSecureRequest()

  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | string | null>(null)
  const [localAttachments, setLocalAttachments] = useState<TStorageMovementAttachment[]>(value)

  // Synchronize internal state with external value if controlled
  React.useEffect(() => {
    if (Array.isArray(value)) {
      setLocalAttachments(value)
    }
  }, [value])

  const attachmentsList = Array.isArray(localAttachments) ? localAttachments : []

  const updateList = useCallback(
    (newList: TStorageMovementAttachment[]) => {
      setLocalAttachments(newList)
      onChange?.(newList)
    },
    [onChange]
  )

  const beforeUpload = useCallback(
    (file: RcFile, fileList: RcFile[]) => {
      const ext = file.name.split(".").pop()?.toLowerCase() || ""
      const isAllowed = ALLOWED_MIME_TYPES.has(file.type) || ["pdf", "jpg", "jpeg", "png", "webp", "gif", "svg", "xlsx", "xls", "doc", "docx", "csv", "txt"].includes(ext)
      if (!isAllowed) {
        message.error(`"${file.name}" no es un formato soportado (solo PDF, imágenes, Excel, Word).`)
        return Upload.LIST_IGNORE
      }
      const sizeMB = file.size / 1024 / 1024
      if (sizeMB > MAX_FILE_SIZE_MB) {
        message.error(`"${file.name}" supera el límite de ${MAX_FILE_SIZE_MB} MB.`)
        return Upload.LIST_IGNORE
      }
      if (attachmentsList.length + fileList.length > MAX_ATTACHMENTS_COUNT) {
        message.warning(`Solo se permite un máximo de ${MAX_ATTACHMENTS_COUNT} archivos adjuntos.`)
        return Upload.LIST_IGNORE
      }
      return true
    },
    [attachmentsList.length]
  )

  const handleUploadFile = useCallback(
    async (file: File) => {
      if (!company.current?.uid) {
        message.error("No se pudo identificar la empresa actual")
        return
      }

      const timestamp = Date.now()
      const sanitizedName = file.name.replaceAll(/[^a-zA-Z0-9._-]/g, "_")
      const folder = movementUid || (movementId ? `movement_${movementId}` : `new_${timestamp}`)
      const storagePath = `${company.current.uid}/movements/${folder}/${timestamp}_${sanitizedName}`

      try {
        setUploading(true)
        const { url, storagePath: finalPath } = await fileStorageService.uploadFile(
          tokenData,
          MOVEMENT_ATTACHMENTS_BUCKET,
          storagePath,
          file,
          true
        )

        const detectedDescription =
          defaultDocType === "PURCHASE_RECEIPT" || defaultDocType === "PURCHASE_ORDER"
            ? "Factura de compra"
            : defaultDocType === "SALE_INVOICE"
            ? "Comprobante de entrega"
            : defaultDocType === "TRANSFER"
            ? "Guía de despacho"
            : "Documento soporte"

        const newAttachment: TStorageMovementAttachment = {
          document_id: movementId,
          uid: `att_${timestamp}_${Math.random().toString(36).substring(2, 7)}`,
          file_name: file.name,
          file_type: file.type || "application/octet-stream",
          file_url: url,
          storage_path: finalPath,
          description: detectedDescription,
          file_size: file.size,
          uploaded_by_user_id: user?.id || null,
          uploaded_by_user_name: user?.name || "",
          created_at: new Date().toISOString(),
        }

        // If directApi mode and movement exists, save directly through backend API
        if (directApi && (movementId || movementUid)) {
          const res = await securePost("/inventory/movement/add-movement-attachment", {
            company: company.current.uid,
            uidMovement: movementUid,
            attachment: newAttachment,
          })
          if (res.status === 200 && res.attachment) {
            const nextList = [...attachmentsList, res.attachment]
            updateList(nextList)
            message.success(`Documento "${file.name}" adjuntado exitosamente`)
          } else {
            throw new Error(res.message || "Error al registrar el adjunto")
          }
        } else {
          // Form / controlled mode
          const nextList = [...attachmentsList, newAttachment]
          updateList(nextList)
          message.success(`Documento "${file.name}" cargado`)
        }
      } catch (err: any) {
        message.error(err.message || "Error al subir el archivo adjunto")
      } finally {
        setUploading(false)
      }
    },
    [company, movementUid, movementId, tokenData, defaultDocType, user, directApi, securePost, attachmentsList, updateList]
  )

  const handleDeleteAttachment = async (index: number) => {
    const target = attachmentsList[index]
    if (!target) return

    const key = target.id || target.uid || index
    setDeletingId(key)

    try {
      // 1. Delete physical file from storage bucket
      if (target.storage_path) {
        await fileStorageService
          .deleteFile(tokenData, {
            bucket: MOVEMENT_ATTACHMENTS_BUCKET,
            paths: [target.storage_path],
          })
          .catch(() => {})
      }

      // 2. If directApi mode and target is already in database, call delete endpoint
      if (directApi && target.id && target.id > 0) {
        const res = await securePost("/inventory/movement/delete-movement-attachment", {
          company: company.current?.uid,
          uidMovement: movementUid,
          id: target.id,
        })
        if (res.status !== 200 && !res.success) {
          throw new Error(res.message || "Error al eliminar el adjunto")
        }
      }

      // 3. Update local list
      const remaining = attachmentsList.filter((_, idx) => idx !== index)
      updateList(remaining)
      message.success("Adjunto eliminado correctamente")
    } catch (err: any) {
      message.error(err.message || "Error al eliminar el adjunto")
    } finally {
      setDeletingId(null)
    }
  }

  const handleChangeDescription = (index: number, newDesc: string) => {
    const updated = attachmentsList.map((att, idx) => (idx === index ? { ...att, description: newDesc } : att))
    updateList(updated)
  }

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <Space size={6}>
          <PaperClipOutlined style={{ color: "#1890ff", fontSize: 16 }} />
          <Text strong style={{ fontSize: 13 }}>
            {title}
          </Text>
          <Tag color="default" style={{ borderRadius: 10, fontSize: 11 }}>
            {attachmentsList.length} / {MAX_ATTACHMENTS_COUNT}
          </Tag>
        </Space>
        <Text type="secondary" style={{ fontSize: 11 }}>
          PDF, Imágenes, Word, Excel (Máx. {MAX_FILE_SIZE_MB}MB)
        </Text>
      </div>

      {/* Lista de Documentos */}
      {attachmentsList.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Row gutter={[10, 10]}>
            {attachmentsList.map((item, index) => {
              const cat = getFileCategory(item.file_type || "", item.file_name)
              const isDeleting = deletingId === (item.id || item.uid || index)
              const isImage = cat.type === "image"

              return (
                <Col xs={24} sm={12} md={12} lg={8} key={item.uid || item.id || index}>
                  <Card
                    size="small"
                    hoverable
                    style={{
                      borderRadius: 8,
                      border: `1px solid ${cat.borderColor}`,
                      background: "#ffffff",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                      overflow: "hidden",
                    }}
                    styles={{
                      body: {
                        padding: "10px 12px",
                      },
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      {/* Icono o Miniatura */}
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 6,
                          background: cat.bgColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          overflow: "hidden",
                          border: `1px solid ${cat.borderColor}`,
                        }}
                      >
                        {isImage ? (
                          <Image
                            src={item.file_url}
                            alt={item.file_name}
                            style={{ width: 44, height: 44, objectFit: "cover" }}
                            preview={{
                              mask: <EyeOutlined style={{ fontSize: 12 }} />,
                            }}
                            fallback="assets/images/no-data-image.svg"
                          />
                        ) : (
                          cat.icon
                        )}
                      </div>

                      {/* Info del archivo */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                          <Tag
                            style={{
                              fontSize: 10,
                              lineHeight: "16px",
                              padding: "0 4px",
                              margin: 0,
                              borderRadius: 4,
                              fontWeight: 600,
                              color: cat.color,
                              background: cat.bgColor,
                              border: `1px solid ${cat.borderColor}`,
                            }}
                          >
                            {cat.label}
                          </Tag>
                          {item.file_size ? (
                            <Text type="secondary" style={{ fontSize: 10 }}>
                              {formatFileSize(item.file_size)}
                            </Text>
                          ) : null}
                        </div>

                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#262626",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            marginTop: 2,
                          }}
                          title={item.file_name}
                        >
                          {item.file_name}
                        </div>

                        {/* Selector/Input de Tipo o Descripción */}
                        {!disabled ? (
                          <div style={{ marginTop: 4 }}>
                            <Select
                              size="small"
                              variant="borderless"
                              value={item.description || "Factura de compra"}
                              onChange={(val) => handleChangeDescription(index, val)}
                              style={{ width: "100%", fontSize: 11, padding: 0, color: "#595959" }}
                              options={DEFAULT_DESCRIPTION_OPTIONS}
                              popupMatchSelectWidth={false}
                            />
                          </div>
                        ) : (
                          item.description && (
                            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
                              {item.description}
                            </Text>
                          )
                        )}
                      </div>
                    </div>

                    {/* Acciones */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 8,
                        paddingTop: 6,
                        borderTop: "1px dashed #f0f0f0",
                      }}
                    >
                      <Tooltip title="Ver o descargar archivo">
                        <Button
                          type="link"
                          size="small"
                          icon={<DownloadOutlined />}
                          href={item.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ padding: "0 6px", height: 22, fontSize: 11 }}
                        >
                          Abrir
                        </Button>
                      </Tooltip>

                      {!disabled && (
                        <Popconfirm
                          title="¿Eliminar este documento adjunto?"
                          onConfirm={() => handleDeleteAttachment(index)}
                          okText="Sí, eliminar"
                          cancelText="Cancelar"
                          placement="topRight"
                        >
                          <Tooltip title="Eliminar adjunto">
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              loading={isDeleting}
                              style={{ padding: "0 4px", height: 22 }}
                            />
                          </Tooltip>
                        </Popconfirm>
                      )}
                    </div>
                  </Card>
                </Col>
              )
            })}
          </Row>
        </div>
      )}

      {/* Zona de Carga (Dragger o Botón) */}
      {!disabled && attachmentsList.length < MAX_ATTACHMENTS_COUNT && (
        <Dragger
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.svg,.xlsx,.xls,.doc,.docx,.csv,.txt"
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={({ file }) => handleUploadFile(file as File)}
          disabled={uploading}
          style={{
            background: "#fafafa",
            border: "1px dashed #d9d9d9",
            borderRadius: 8,
            padding: attachmentsList.length > 0 ? "12px 16px" : "20px 16px",
          }}
        >
          {attachmentsList.length === 0 ? (
            <div>
              <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                <InboxOutlined style={{ color: "#1890ff", fontSize: 30 }} />
              </p>
              <p className="ant-upload-text" style={{ fontSize: 13, fontWeight: 500, margin: "0 0 4px" }}>
                Haz clic o arrastra aquí facturas, remisiones o soportes de este movimiento
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 11, color: "#8c8c8c", margin: 0 }}>
                Formatos permitidos: PDF, JPG, PNG, Excel, Word (Hasta {MAX_FILE_SIZE_MB}MB por archivo)
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <PlusOutlined style={{ color: "#1890ff" }} />
              <span style={{ fontSize: 12, color: "#1890ff", fontWeight: 500 }}>
                {uploading ? "Subiendo archivo..." : "Adjuntar otro documento o factura"}
              </span>
            </div>
          )}
        </Dragger>
      )}
    </div>
  )
}

export default MovementAttachmentUploader
